/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import { phoenixChatUpdateCustomerSavedProperties } from '../queries';
import { phoenixShopUpdateCustomerSavedProperties } from '@marriott/mi-shop-graphql';
import { useSessionUpdateZustandStore } from '../modules/store/sessionUpdateZustandStore';
import { useSavedPropertiesDispatchStore } from '../modules/store/savedPropertiesDispatchZustandStore';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { getDTTShopResult } from '../organisms/OSearchResults/lib/shop.dtt.api';

interface UseSavedPropertiesOptions {
  pageVal?: string;
  requestId?: string;
  skipQuery?: boolean;
}

interface SavedProperty {
  id: string;
}

export const useSavedProperties = (options: UseSavedPropertiesOptions = {}) => {
  const { pageVal = 'chat', requestId: requestIdProp, skipQuery = false } = options;

  const apolloClient = useApolloClient();
  const { getCustomerToken } = getDTTShopResult(apolloClient);

  const clientEnvVars = useClientEnvVarsStore((state: any) => state.envVarsObject);
  const { revisionToken, setRevisionToken, sessionObject } = useSessionUpdateZustandStore();

  // Get requestId with fallback to env vars
  const requestId = requestIdProp || clientEnvVars?.['REQUEST_ID'] || '';

  // Use selector pattern for better reactivity
  const savedPropertiesFromStore = useSavedPropertiesDispatchStore(state => state.savedData.savedPropertiesList);
  const hasFetchedInitialFromStore = useSavedPropertiesDispatchStore(state => state.savedData.hasFetchedInitial);
  const dispatchToStore = useSavedPropertiesDispatchStore(state => state.dispatch);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [isSavedDataStaleError, setIsSavedDataStaleError] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  // Use a ref to track if we've initiated a fetch for the current session
  const fetchInitiatedRef = useRef<string | null>(null);

  // Fetch saved properties from the API
  const fetchSavedProperties = useCallback(async () => {
    if (
      !sessionObject?.accessToken ||
      !sessionObject?.consumerID ||
      skipQuery ||
      hasFetchedInitialFromStore ||
      isFetching
    ) {
      return;
    }

    setIsFetching(true);
    setIsLoading(true);
    setError(null);

    try {
      const { data } = await getCustomerToken({
        variables: {
          customerId: sessionObject.consumerID,
        },
        context: {
          headers: {
            'x-request-id': requestId || '',
            Authorization: `${clientEnvVars['APOLLOGRAPHQL_AUTH_TOKEN']}, Bearer ${sessionObject.accessToken}`,
          },
        },
      });

      const tempSavedArray: SavedProperty[] = [];
      data?.customer?.savedProperties?.forEach((item: { id: any }) => {
        tempSavedArray.push({ id: item.id });
      });

      dispatchToStore({ type: 'populateState', payload: tempSavedArray });
    } catch (err) {
      setError(err);
      console.error('Error fetching saved properties:', err);
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  }, [
    sessionObject,
    skipQuery,
    hasFetchedInitialFromStore,
    isFetching,
    getCustomerToken,
    requestId,
    clientEnvVars,
    dispatchToStore,
  ]);

  // Update properties mutation
  const [updatePropertiesMutation] = useMutation(
    pageVal === 'chat' ? phoenixChatUpdateCustomerSavedProperties : phoenixShopUpdateCustomerSavedProperties,
    {
      onCompleted: data => {
        if (data?.updateCustomerSavedProperties?.status[0]?.httpStatus === 412) {
          // Stale error - revision token mismatch
          setIsSavedDataStaleError(true);
          setError(new Error('Stale data error - revision token mismatch'));
        } else if (data?.updateCustomerSavedProperties?.status[0]?.httpStatus === 200) {
          // Success - update revision token and sync store with backend response
          setRevisionToken(data.updateCustomerSavedProperties.revisionToken);
          setIsSavedDataStaleError(false);

          const savedPropertiesArrayWithId: SavedProperty[] = [];
          data.updateCustomerSavedProperties.savedProperties?.forEach((idValue: any) =>
            savedPropertiesArrayWithId.push({ id: idValue.id })
          );

          // Update store with actual backend data (source of truth)
          dispatchToStore({
            type: 'populateState',
            payload: savedPropertiesArrayWithId,
          });
        } else {
          // Other error status codes
          setError(
            new Error(`Update failed with status: ${data?.updateCustomerSavedProperties?.status[0]?.httpStatus}`)
          );
        }
      },
      onError: error => {
        console.error('[useSavedProperties] Mutation error:', error);
        setError(error);
      },
    }
  );

  // Check if a property is saved
  const isPropertySaved = useCallback(
    (propertyId: string) => {
      return savedPropertiesFromStore.some(prop => prop.id?.toLowerCase() === propertyId?.toLowerCase());
    },
    [savedPropertiesFromStore]
  );

  // Toggle save/unsave property
  const toggleSaveProperty = useCallback(
    async (propertyId: string) => {
      if (!sessionObject?.consumerID || !sessionObject?.accessToken) {
        console.error('[useSavedProperties] Cannot save - missing session data');
        console.error('[useSavedProperties] sessionObject:', sessionObject);
        return;
      }

      const isSaved = isPropertySaved(propertyId);

      // Build the new list of saved properties
      let newSavedProperties: SavedProperty[];

      if (isSaved) {
        // Remove the property
        newSavedProperties = savedPropertiesFromStore.filter(
          prop => prop.id?.toLowerCase() !== propertyId.toLowerCase()
        );
      } else {
        // Add the property
        newSavedProperties = [...savedPropertiesFromStore, { id: propertyId }];
      }

      // Get the revision token
      let finalRevisionToken = revisionToken;

      // If local state is empty, try to get from Zustand store
      if (!finalRevisionToken || finalRevisionToken === '') {
        const zustandRevisionToken = useSessionUpdateZustandStore.getState().revisionToken;
        if (zustandRevisionToken) {
          finalRevisionToken = zustandRevisionToken;
        }
      }

      // Handle case where revisionToken might be an object
      if (typeof finalRevisionToken === 'object' && (finalRevisionToken as any)?.revisionToken) {
        finalRevisionToken = (finalRevisionToken as any).revisionToken;
      }

      // Call the mutation
      try {
        await updatePropertiesMutation({
          variables: {
            input: {
              id: sessionObject.consumerID,
              revisionToken: finalRevisionToken || '',
              savedProperties: newSavedProperties,
            },
          },
          context: {
            headers: {
              'x-request-id': requestId || '',
              Authorization: `${clientEnvVars['APOLLOGRAPHQL_AUTH_TOKEN']}, Bearer ${sessionObject.accessToken}`,
            },
          },
        });
      } catch (error) {
        console.error('[useSavedProperties] Error calling mutation:', error);
      }
    },
    [
      sessionObject,
      isPropertySaved,
      savedPropertiesFromStore,
      revisionToken,
      updatePropertiesMutation,
      requestId,
      clientEnvVars,
    ]
  );

  // Fetch initial saved properties when session is available
  useEffect(() => {
    // Create a unique key for this session
    const sessionKey =
      sessionObject?.consumerID && sessionObject?.accessToken
        ? `${sessionObject.consumerID}-${sessionObject.accessToken}`
        : null;

    // Check if we should fetch
    if (sessionKey && !skipQuery && !hasFetchedInitialFromStore && fetchInitiatedRef.current !== sessionKey) {
      // Mark that we've initiated a fetch for this session
      fetchInitiatedRef.current = sessionKey;
      fetchSavedProperties();
    }
  }, [
    sessionObject?.accessToken,
    sessionObject?.consumerID,
    skipQuery,
    hasFetchedInitialFromStore,
    fetchSavedProperties,
  ]);

  return {
    savedProperties: savedPropertiesFromStore,
    isLoading,
    error,
    isSavedDataStaleError,
    isPropertySaved,
    toggleSaveProperty,
    fetchSavedProperties,
    hasFetchedInitial: hasFetchedInitialFromStore,
  };
};
